import type { ClientGameState, GameEvent } from '../shared/types';

export const RESUME_KEY = 'spades-active-game';

export type Snapshot = {
  seq: number;
  gameState: ClientGameState;
  events?: GameEvent[];
};

export type ApplyListener = (snap: Snapshot, isNew: boolean) => void;

let appliedSeq = -1;
let gameState: ClientGameState | null = null;
let listeners: ApplyListener[] = [];

export let playerId: number | null = null;
export let playerName = '';
export let isAdmin = false;
export let currentGameId: string | null = null;
export let viewerSeat = 0;
export let playInFlight = false;
export let bidInFlight = false;

export function setPlayer(id: number | null, name: string, admin: boolean): void {
  playerId = id;
  playerName = name;
  isAdmin = admin;
}

export function setGame(id: string | null, seat: number): void {
  currentGameId = id;
  viewerSeat = seat;
  if (id) persistActiveGame(id, seat);
  else clearActiveGame();
}

export function setPlayInFlight(v: boolean): void {
  playInFlight = v;
}

export function setBidInFlight(v: boolean): void {
  bidInFlight = v;
}

export function getState(): ClientGameState | null {
  return gameState;
}

/** Local paint only — does not advance seq, so the next server snapshot still applies. */
export function paintLocal(state: ClientGameState): void {
  gameState = state;
}

export function getAppliedSeq(): number {
  return appliedSeq;
}

export function resetStore(): void {
  appliedSeq = -1;
  gameState = null;
}

export function onApply(fn: ApplyListener): void {
  listeners.push(fn);
}

/** Apply only if `seq` is newer. Returns whether the board moved. */
export function apply(snap: Snapshot): boolean {
  if (snap.seq <= appliedSeq) return false;
  appliedSeq = snap.seq;
  gameState = snap.gameState;
  viewerSeat = snap.gameState.viewerSeat ?? viewerSeat;
  // The server has the play (or bid). Do not keep the hand locked waiting
  // for a POST that is stuck behind a stalled HTTP/2 stream.
  playInFlight = false;
  bidInFlight = false;
  for (const fn of listeners) fn(snap, true);
  return true;
}

export function persistActiveGame(gameId: string, seat: number): void {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ gameId, viewerSeat: seat }));
  } catch { /* ignore */ }
}

export function clearActiveGame(): void {
  try { sessionStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
}

export function readResumeTarget(): { gameId: string; viewerSeat?: number } | null {
  const fromUrl = new URLSearchParams(location.search).get('resume');
  if (fromUrl && /^[0-9a-f-]{36}$/i.test(fromUrl)) return { gameId: fromUrl };
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { gameId?: string; viewerSeat?: number };
    if (parsed.gameId && /^[0-9a-f-]{36}$/i.test(parsed.gameId)) return parsed as { gameId: string; viewerSeat?: number };
  } catch { /* ignore */ }
  return null;
}
